import { Link, generatePath } from 'react-router';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { paths } from '../../../../common/constants/pathVariables.js';
import { useSearchParams } from '../../../../common/hooks/useSearchParams.js';
import { identifierMsg, t, topicMsg } from '../../../../common/i18n/index.js';
import { getArrayPropertySorter } from '../../../../common/utils/arrayUtils.js';
import { formatTimeToYYYYMMDDHHmm } from '../../../../common/utils/dateUtils.js';
import { Grid } from '../../../Grid/Grid.js';
import { tableSearchRow } from '../../../Table/TableDataUtil.js';
import { DEFAULT_ITEMS_PER_PAGE, ListPagination, ListSearch, Table } from '../../../Table/index.js';
import { resetPagingParams, setDefaultItemsPerPage } from '../../../Table/tableUtils.js';
import { getDnsRecordActionMessage } from '../../dnsManagementUtils.js';
import type { CLTableColumn, CLTableRow } from '../../../../common/types/componentLibrary';
import type { DnsRecordsHistoryResponse } from '../../../../generated/api/models.js';
import type { DnsRecordsSearchParams } from '../../dnsManagementUtils.js';

import './DnsHistoryList.scss';

export const DnsHistoryList = ({
  historyData: { nextCursor, prevCursor, records, total },
  subscriptionId,
}: {
  historyData: DnsRecordsHistoryResponse;
  subscriptionId: string;
}) => {
  const searchParams = useSearchParams<DnsRecordsSearchParams>();
  const { sort, search, order } = searchParams;

  const columns = [
    { key: 'name', label: t.G0NE(identifierMsg), sortable: false },
    { key: 'type', label: t.F9G1('Type'), sortable: false },
    { key: 'action', label: t.JJTS(topicMsg), sortable: true },
    { key: 'id', label: t.Q1DM('Time of edit'), sortable: true }, // Use `id` instead of `created` for sorting
    { key: 'author', label: t.AD92('Editor'), sortable: true },
  ] satisfies CLTableColumn[];

  const rows = records
    .map(row => ({
      id: row.id,
      name: row.name,
      type: row.type,
      action: row.action,
      created: row.created,
      author: row.author,
    }))
    .filter(row => tableSearchRow(search, row))
    .sort(getArrayPropertySorter(sort, order))
    .map(row => ({
      name: (
        <Link to={generatePath(paths.PS_ELISA_DNS_DOMAIN_HISTORY_ITEM, { subscriptionId, historyId: row.id })}>
          {row.name || '-'}
        </Link>
      ),
      type: row.type,
      action: getDnsRecordActionMessage(row.action),
      id: formatTimeToYYYYMMDDHHmm(row.created),
      author: row.author,
    })) satisfies CLTableRow[];

  return (
    <Grid>
      <div className={`of-dns-history-list ${dsClass.MARGIN_VERTICAL_3}`}>
        <ListSearch
          onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
        />
        <Table
          columns={columns}
          noItemsText={t.VA1O('No records')}
          onSortChange={() => resetPagingParams(new URLSearchParams(searchParams), true)}
          rows={rows}
        />
        <ListPagination totalItems={total} cursorNavigation={true} prevCursor={prevCursor} nextCursor={nextCursor} />
      </div>
    </Grid>
  );
};
